import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { FromNow } from '@plato/app-shared/components/from-now';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { IdAndNameCell } from '@plato/ui-legacy/components/list-views/id-and-name-cell';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import type { ColumnDef } from '@plato/ui/components/data-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type SalesDraftOrderListItem,
    type SalesDraftSortField,
    salesDraftSortField,
} from '../../../server/types/drafts-types.ts';
import { DraftListActionDropdown } from '../components/draft-list-action-dropdown.tsx';

export function useDraftListColumns() {
    const { t } = useTranslation();

    const columns: ColumnDef<SalesDraftOrderListItem, never, SalesDraftSortField>[] = useMemo(() => {
        return [
            {
                accessorKey: 'customer',
                id: 'CUSTOMER',
                header: t('sales_draft__list_view__header__customer', {
                    ns: 'sales-draft',
                }),
                cell: ({ row, cell }) => (
                    <IdAndNameCell
                        to={`/customers/${encodeURIComponent(cell.row.original.customer_id)}`}
                        id={row.original.customer_id}
                        title={row.original.customer_name}
                    />
                ),
                size: 250,
                meta: {
                    aiChat: { label: t('sales_draft__list_view__header__customer', { ns: 'sales-draft' }) },
                    pinned: 'left',
                },
            },
            {
                accessorKey: 'order_created_at_date',
                id: 'CREATED_AT',
                header: t('sales_draft__list_view__header__creation_date', {
                    ns: 'sales-draft',
                }),
                cell: ({ row }) => (
                    <DoubleDeckerCell
                        align="left"
                        topClassName="text-neutral-500"
                        topLevel={<FromNow>{row.original.created_at}</FromNow>}
                        bottomLevel={<DateText date={row.original.created_at} />}
                    />
                ),
                meta: {
                    aiChat: {
                        label: t('sales_draft__list_view__header__creation_date', { ns: 'sales-draft' }),
                    },
                    sortBy: salesDraftSortField.CREATED_AT,
                },
            },
            {
                accessorKey: 'total_amount',
                id: 'total_amount',
                header: t('sales_draft__list_view__header__total', {
                    ns: 'sales-draft',
                }),
                meta: {
                    aiChat: { label: t('sales_draft__list_view__header__total', { ns: 'sales-draft' }) },
                },
                cell: ({ cell }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{cell.getValue() as string}</CurrencyOrEmpty>
                    </DataText>
                ),
                enableSorting: false,
            },
            {
                accessorKey: 'normal_line_item',
                header: t('sales_draft__list_view__header__positions', {
                    ns: 'sales-draft',
                }),
                meta: {
                    aiChat: {
                        label: t('sales_draft__list_view__header__positions', { ns: 'sales-draft' }),
                    },
                },
                cell: ({ row }) => (
                    <DataText>
                        <NumberOrEmpty>{row.original.line_items.length}</NumberOrEmpty>
                    </DataText>
                ),
                enableSorting: false,
            },
            {
                accessorKey: 'creator',
                id: 'CREATED_BY',
                header: t('sales_draft__list_view__header__created_by', {
                    ns: 'sales-draft',
                }),
                cell: ({ row }) => row.original.creator.fullname,
                meta: {
                    aiChat: {
                        label: t('sales_draft__list_view__header__created_by', { ns: 'sales-draft' }),
                    },
                    sortBy: salesDraftSortField.CREATED_BY,
                },
            },
            {
                id: 'options',
                accessorKey: 'options',
                header: () => null,
                cell: ({ row }) => <DraftListActionDropdown draft={row.original} />,
                size: 1,
                enableSorting: false,
            },
        ];
    }, [t]);

    return columns;
}
